import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  TextField,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Chip,
  Tooltip,
  styled,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Comment as CommentIcon,
  Send as SendIcon,
  ThumbUp as ThumbUpIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
  FullscreenExit as FullscreenExitIcon,
  Fullscreen as FullscreenIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  GridView as GridViewIcon,
  ViewCarousel as ViewCarouselIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { collection, addDoc, getDocs, query, orderBy, updateDoc, doc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';

// Interface for comment
interface Comment {
  id: string;
  text: string;
  createdAt: Date;
  createdBy: {
    id: string;
    name: string;
    photoUrl?: string;
  };
  likes: string[];
}

// Interface for gallery item
interface GalleryItem {
  url: string;
  caption?: string;
  comments?: Comment[];
  dateAdded?: Date;
  addedBy?: {
    id: string;
    name: string;
    photoUrl?: string;
  };
}

// Props interface
interface OrgTimelineGalleryProps {
  open: boolean;
  onClose: () => void;
  images: string[];
  initialIndex?: number;
  itemId: string;
  itemType: 'event' | 'trip';
  organizationId: string;
  fetchComments?: boolean;
  onCommentAdded?: () => void;
}

const ImageWithFallback = ({ src, alt, style, className, onLoad, onError }: {
  src: string;
  alt?: string;
  style?: React.CSSProperties;
  className?: string;
  onLoad?: () => void;
  onError?: () => void;
}) => {
  const [imgSrc, setImgSrc] = useState<string>(src);
  const [hasError, setHasError] = useState<boolean>(false);
  
  useEffect(() => {
    // Reset error state when src changes
    setImgSrc(src);
    setHasError(false);
  }, [src]);

  const handleError = () => {
    if (!hasError) {
      console.error('Image failed to load:', src);
      setHasError(true);
      setImgSrc('/images/placeholder.jpg'); // Default fallback
      if (onError) onError();
    }
  };

  const handleLoad = () => {
    if (onLoad) onLoad();
  };

  return (
    <Box 
      sx={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%'
      }}
    >
      <img
        src={imgSrc}
        alt={alt || 'Gallery image'}
        style={style}
        className={className}
        onError={handleError}
        onLoad={handleLoad}
      />
      {hasError && (
        <Box 
          sx={{ 
            position: 'absolute',
            bottom: 0,
            width: '100%',
            textAlign: 'center',
            bgcolor: 'rgba(0,0,0,0.5)',
            color: 'white',
            padding: 1
          }}
        >
          <Typography variant="caption">
            <InfoIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
            Image could not be loaded
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const FullscreenImage = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '80vh',
  width: '100%',
  '& img': {
    maxHeight: '100%',
    maxWidth: '100%',
    objectFit: 'contain',
  }
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: 'rgba(0, 0, 0, 0.3)',
  color: '#fff',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
}));

const OrgTimelineGallery: React.FC<OrgTimelineGalleryProps> = ({
  open,
  onClose,
  images,
  initialIndex = 0,
  itemId,
  itemType,
  organizationId,
  fetchComments = true,
  onCommentAdded
}) => {
  const { currentUser } = useAuth();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [showComments, setShowComments] = useState(true);
  const [viewMode, setViewMode] = useState('carousel');

  useEffect(() => {
    if (open && images.length > 0) {
      // Filter out any invalid URLs before processing
      const validImages = images.filter(url => {
        // Basic validation - must be a string and start with http/https or data:image
        const isValid = typeof url === 'string' && 
          (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:image/'));
        
        // Log invalid URLs to help debugging
        if (!isValid && url) {
          console.warn('Filtered out invalid image URL:', url);
        }
        
        return isValid;
      });


      
      if (validImages.length === 0) {
        console.error('No valid images available for gallery');
        // Add a default placeholder if no valid images
        setGalleryItems([{
          url: '/images/placeholder.jpg', 
          caption: 'No valid images available'
        }]);
      } else {
        // Convert array of image URLs to gallery items
        const items: GalleryItem[] = validImages.map(url => ({ url }));
        setGalleryItems(items);
      }
      
      // Fetch comments if needed
      if (fetchComments) {
        fetchImageComments();
      }
      
      setLoading(false);
    }
  }, [open, images, itemId, itemType, fetchComments]);

  const fetchImageComments = async () => {
    try {
      setLoading(true);
      const commentsRef = collection(db, `${itemType}s`, itemId, 'gallery_comments');
      const commentsQuery = query(commentsRef, orderBy('createdAt', 'desc'));
      const commentsSnapshot = await getDocs(commentsQuery);
      
      // Group comments by image URL
      const commentsByImage: Record<string, Comment[]> = {};
      
      commentsSnapshot.forEach(doc => {
        const data = doc.data();
        const imageUrl = data.imageUrl || '';
        
        if (!commentsByImage[imageUrl]) {
          commentsByImage[imageUrl] = [];
        }
        
        commentsByImage[imageUrl].push({
          id: doc.id,
          text: data.text,
          createdAt: data.createdAt instanceof Timestamp 
            ? data.createdAt.toDate() 
            : new Date(data.createdAt),
          createdBy: {
            id: data.createdBy.id,
            name: data.createdBy.name,
            photoUrl: data.createdBy.photoUrl
          },
          likes: data.likes || []
        });
      });
      
      // Update gallery items with comments
      setGalleryItems(prevItems => {
        return prevItems.map(item => ({
          ...item,
          comments: commentsByImage[item.url] || []
        }));
      });
    } catch (error) {
      console.error('Error fetching image comments:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleNext = () => {
    if (currentIndex < images.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      // Loop back to the beginning
      setCurrentIndex(0);
    }
  };
  
  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else {
      // Loop to the end
      setCurrentIndex(images.length - 1);
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') {
      handleNext();
    } else if (e.key === 'ArrowLeft') {
      handlePrevious();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };
  
  const handleAddComment = async () => {
    if (!currentUser || !newComment.trim()) return;
    
    try {
      setSubmittingComment(true);
      const currentImage = images[currentIndex];
      
      const commentsRef = collection(db, `${itemType}s`, itemId, 'gallery_comments');
      await addDoc(commentsRef, {
        text: newComment,
        imageUrl: currentImage,
        createdAt: serverTimestamp(),
        createdBy: {
          id: currentUser.uid,
          name: currentUser.displayName || 'Anonymous User',
          photoUrl: currentUser.photoURL || undefined
        },
        likes: []
      });
      
      // Add comment to local state
      setGalleryItems(prevItems => {
        const updatedItems = [...prevItems];
        const imageIndex = updatedItems.findIndex(item => item.url === currentImage);
        
        if (imageIndex !== -1) {
          const newCommentObj: Comment = {
            id: Date.now().toString(), // Temporary ID until we refresh
            text: newComment,
            createdAt: new Date(),
            createdBy: {
              id: currentUser.uid,
              name: currentUser.displayName || 'Anonymous User',
              photoUrl: currentUser.photoURL || undefined
            },
            likes: []
          };
          
          updatedItems[imageIndex].comments = 
            [...(updatedItems[imageIndex].comments || []), newCommentObj];
        }
        
        return updatedItems;
      });
      
      // Reset comment input
      setNewComment('');
      
      // Call callback if provided
      if (onCommentAdded) {
        onCommentAdded();
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  };
  
  const handleLikeComment = async (commentId: string) => {
    if (!currentUser) return;
    
    try {
      const currentImage = images[currentIndex];
      const commentRef = doc(db, `${itemType}s`, itemId, 'gallery_comments', commentId);
      
      // Find the comment in our local state
      const galleryItem = galleryItems.find(item => item.url === currentImage);
      const comment = galleryItem?.comments?.find(c => c.id === commentId);
      
      if (comment) {
        const likes = [...comment.likes];
        const userIndex = likes.indexOf(currentUser.uid);
        
        // Toggle like
        if (userIndex > -1) {
          likes.splice(userIndex, 1);
        } else {
          likes.push(currentUser.uid);
        }
        
        // Update in Firestore
        await updateDoc(commentRef, { likes });
        
        // Update in local state
        setGalleryItems(prevItems => {
          return prevItems.map(item => {
            if (item.url === currentImage) {
              return {
                ...item,
                comments: item.comments?.map(c => {
                  if (c.id === commentId) {
                    return { ...c, likes };
                  }
                  return c;
                })
              };
            }
            return item;
          });
        });
      }
    } catch (error) {
      console.error('Error liking comment:', error);
    }
  };
  
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    // Reset zoom when toggling fullscreen
    setZoom(1);
  };
  
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3));
  };
  
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.5));
  };
  
  const currentImage = images[currentIndex];
  const currentGalleryItem = galleryItems.find(item => item.url === currentImage);
  
  const renderCarousel = () => {
    if (galleryItems.length === 0) return null;
    
    const currentItem = galleryItems[currentIndex];
    
    return (
      <Box sx={{ position: 'relative', width: '100%', textAlign: 'center' }}>
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          position: 'relative'
        }}>
          {/* Only show navigation if we have more than one image */}
          {galleryItems.length > 1 && currentIndex > 0 && (
            <IconButton 
              onClick={handlePrevious}
              sx={{ position: 'absolute', left: 0, zIndex: 1, color: 'white', bgcolor: 'rgba(0,0,0,0.3)', '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' } }}
            >
              <ChevronLeftIcon />
            </IconButton>
          )}
          
          <FullscreenImage>
            <ImageWithFallback
              src={currentItem.url}
              alt={currentItem.caption || `Image ${currentIndex + 1}`}
              style={{ transform: `scale(${zoom})`, transition: 'transform 0.3s ease' }}
            />
          </FullscreenImage>
          
          {/* Only show navigation if we have more than one image */}
          {galleryItems.length > 1 && currentIndex < galleryItems.length - 1 && (
            <IconButton 
              onClick={handleNext}
              sx={{ position: 'absolute', right: 0, zIndex: 1, color: 'white', bgcolor: 'rgba(0,0,0,0.3)', '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' } }}
            >
              <ChevronRightIcon />
            </IconButton>
          )}
        </Box>
        
        {/* Image counter */}
        <Box sx={{ position: 'absolute', top: 16, right: 16, bgcolor: 'rgba(0,0,0,0.5)', color: 'white', px: 1, py: 0.5, borderRadius: 1 }}>
          {currentIndex + 1} / {galleryItems.length}
        </Box>
        
        {/* Caption */}
        {currentItem.caption && (
          <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
            {currentItem.caption}
          </Typography>
        )}
      </Box>
    );
  };

  const renderGridView = () => {
    return (
      <Grid container spacing={2}>
        {galleryItems.map((item, index) => (
          <Grid item xs={6} sm={4} md={3} key={index}>
            <Paper 
              elevation={3} 
              sx={{ 
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'scale(1.02)',
                  transition: 'all 0.2s'
                }
              }}
              onClick={() => {
                setCurrentIndex(index);
                setViewMode('carousel');
              }}
            >
              <Box sx={{ position: 'relative', height: 160, overflow: 'hidden' }}>
                <ImageWithFallback
                  src={item.url}
                  alt={item.caption || `Image ${index + 1}`}
                  style={{ 
                    width: '100%',
                    height: '160px',
                    objectFit: 'cover'
                  }}
                />
                {item.comments && item.comments.length > 0 && (
                  <Chip 
                    icon={<CommentIcon fontSize="small" />}
                    label={item.comments.length}
                    size="small"
                    sx={{ 
                      position: 'absolute', 
                      bottom: 8, 
                      right: 8, 
                      bgcolor: 'rgba(0,0,0,0.6)',
                      color: 'white',
                      '& .MuiChip-icon': {
                        color: 'white'
                      }
                    }}
                  />
                )}
              </Box>
              {item.caption && (
                <Box sx={{ p: 1 }}>
                  <Typography variant="caption" noWrap>
                    {item.caption}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xl"
      fullScreen={isFullscreen}
      onKeyDown={handleKeyDown}
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          borderRadius: isFullscreen ? 0 : 2,
          width: isFullscreen ? '100%' : '90vw',
          height: isFullscreen ? '100%' : 'auto',
          overflow: 'hidden'
        }
      }}
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              p: 1, 
              borderBottom: '1px solid',
              borderColor: 'divider' 
            }}
          >
            <Typography variant="h6">
              {`${itemType === 'event' ? 'Event' : 'Trip'} Gallery (${currentIndex + 1}/${images.length})`}
            </Typography>
            <Box>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => setViewMode(newMode)}
              >
                <ToggleButton value="carousel">
                  <ViewCarouselIcon />
                </ToggleButton>
                <ToggleButton value="grid">
                  <GridViewIcon />
                </ToggleButton>
              </ToggleButtonGroup>
              <Tooltip title="Zoom Out">
                <IconButton onClick={handleZoomOut} disabled={zoom <= 0.5}>
                  <ZoomOutIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Zoom In">
                <IconButton onClick={handleZoomIn} disabled={zoom >= 3}>
                  <ZoomInIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
                <IconButton onClick={toggleFullscreen}>
                  {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
              </Tooltip>
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          
          {viewMode === 'carousel' ? (
            <Box sx={{ 
              display: 'flex', 
              flexGrow: 1,
              position: 'relative',
              overflow: 'auto',
            }}>
              {/* Main image display */}
              <Box sx={{ 
                width: showComments ? 'calc(100% - 350px)' : '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: 2,
                position: 'relative',
                transition: 'width 0.3s ease'
              }}>
                {renderCarousel()}
                
                {/* Navigation buttons */}
                <NavigationButton 
                  onClick={handlePrevious} 
                  sx={{ left: 16 }}
                >
                  <ChevronLeftIcon fontSize="large" />
                </NavigationButton>
                
                <NavigationButton 
                  onClick={handleNext} 
                  sx={{ right: 16 }}
                >
                  <ChevronRightIcon fontSize="large" />
                </NavigationButton>
              </Box>
              
              {/* Comments panel */}
              {showComments && (
                <Box sx={{ 
                  width: 350, 
                  borderLeft: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%'
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    p: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Typography variant="subtitle1">
                      Comments ({currentGalleryItem?.comments?.length || 0})
                    </Typography>
                    <IconButton onClick={() => setShowComments(false)}>
                      <ChevronRightIcon />
                    </IconButton>
                  </Box>
                  
                  <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
                    <List>
                      {currentGalleryItem?.comments?.length ? (
                        currentGalleryItem.comments.map(comment => (
                          <React.Fragment key={comment.id}>
                            <ListItem 
                              alignItems="flex-start"
                              secondaryAction={
                                <IconButton 
                                  edge="end" 
                                  size="small"
                                  onClick={() => handleLikeComment(comment.id)}
                                >
                                  <ThumbUpIcon 
                                    fontSize="small" 
                                    color={comment.likes.includes(currentUser?.uid || '') ? 'primary' : 'action'} 
                                  />
                                  {comment.likes.length > 0 && (
                                    <Typography variant="caption" sx={{ ml: 0.5 }}>
                                      {comment.likes.length}
                                    </Typography>
                                  )}
                                </IconButton>
                              }
                            >
                              <ListItemAvatar>
                                <Avatar src={comment.createdBy.photoUrl} />
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="subtitle2">
                                      {comment.createdBy.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {format(comment.createdAt, 'MMM d, h:mm a')}
                                    </Typography>
                                  </Box>
                                }
                                secondary={comment.text}
                              />
                            </ListItem>
                            <Divider variant="inset" component="li" />
                          </React.Fragment>
                        ))
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            No comments yet. Be the first to comment!
                          </Typography>
                        </Box>
                      )}
                    </List>
                  </Box>
                  
                  <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Add a comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <IconButton 
                            edge="end" 
                            disabled={!newComment.trim() || submittingComment}
                            onClick={handleAddComment}
                          >
                            <SendIcon />
                          </IconButton>
                        ),
                      }}
                    />
                  </Box>
                </Box>
              )}
              
              {/* Button to show comments when hidden */}
              {!showComments && (
                <Box 
                  sx={{
                    position: 'absolute',
                    right: 16,
                    top: 16,
                    zIndex: 1
                  }}
                >
                  <Button 
                    variant="contained" 
                    startIcon={<CommentIcon />}
                    onClick={() => setShowComments(true)}
                  >
                    Comments ({currentGalleryItem?.comments?.length || 0})
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              flexGrow: 1, 
              p: 2
            }}>
              {renderGridView()}
            </Box>
          )}
          
          {/* Thumbnail strip at the bottom */}
          {viewMode === 'carousel' && (
            <Box sx={{ 
              p: 1, 
              borderTop: '1px solid', 
              borderColor: 'divider', 
              overflowX: 'auto',
              display: 'flex',
              gap: 1
            }}>
              {images.map((img, index) => (
                <Box 
                  key={index}
                  component="img"
                  src={img}
                  alt={`Thumbnail ${index + 1}`}
                  sx={{
                    height: 60,
                    width: 80,
                    objectFit: 'cover',
                    cursor: 'pointer',
                    borderRadius: 1,
                    border: index === currentIndex ? '2px solid' : '2px solid transparent',
                    borderColor: index === currentIndex ? 'primary.main' : 'transparent',
                    opacity: index === currentIndex ? 1 : 0.7,
                    '&:hover': {
                      opacity: 1
                    }
                  }}
                  onClick={() => setCurrentIndex(index)}
                />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Dialog>
  );
};

export default OrgTimelineGallery;
